import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Users, BarChart3, ArrowLeft, Plus, Pencil, Trash2, RotateCcw,
  Loader2, Shield, User, MessageSquare, CreditCard, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import * as api from '../lib/api';

export default function AdminPage() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = tab || 'users';

  return (
    <div className="min-h-screen bg-[#0B0B0C]" data-testid="admin-page">
      {/* Top bar */}
      <div className="h-14 flex items-center px-6 border-b border-white/[0.06] bg-[#101013]">
        <Link to="/chat" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mr-6">
          <ArrowLeft size={18} />
          <span className="text-sm">Torna alla chat</span>
        </Link>
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-white/60" />
          <span className="text-white font-semibold text-lg">
            <span className="font-medium">TO</span><span className="font-extrabold">YOU</span>
            <span className="text-white/40 font-normal ml-2 text-sm">Admin</span>
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={(v) => navigate(`/admin/${v}`)}>
          <TabsList className="bg-white/[0.04] border border-white/[0.06] mb-6">
            <TabsTrigger value="users" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <Users size={14} className="mr-2" /> Utenti
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <BarChart3 size={14} className="mr-2" /> Analisi
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
          <TabsContent value="analytics">
            <AnalyticsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'user',
    allowed_models: [], credits_total: 100, monthly_limit: 50,
    daily_message_limit: 100, active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [u, m] = await Promise.all([api.adminGetUsers(), api.adminGetAllModels()]);
      setUsers(u);
      setAllModels(m);
    } catch (e) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditUser(null);
    setFormData({
      name: '', email: '', password: '', role: 'user',
      allowed_models: ['gpt-4o', 'gemini-flash'], credits_total: 100,
      monthly_limit: 50, daily_message_limit: 100, active: true
    });
    setShowDialog(true);
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setFormData({
      name: user.name, email: user.email, password: '', role: user.role,
      allowed_models: user.allowed_models || [],
      credits_total: user.credits_total || 100,
      monthly_limit: user.monthly_limit || 50,
      daily_message_limit: user.daily_message_limit || 100,
      active: user.active !== false
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await api.adminUpdateUser(editUser.id, updateData);
        toast.success('Utente aggiornato');
      } else {
        if (!formData.password) {
          toast.error('Password richiesta');
          return;
        }
        await api.adminCreateUser(formData);
        toast.success('Utente creato');
      }
      setShowDialog(false);
      loadData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Eliminare questo utente?')) return;
    try {
      await api.adminDeleteUser(userId);
      toast.success('Utente eliminato');
      loadData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleResetCredits = async (userId) => {
    try {
      await api.adminResetCredits(userId);
      toast.success('Crediti resettati');
      loadData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleModel = (modelKey) => {
    setFormData(prev => ({
      ...prev,
      allowed_models: prev.allowed_models.includes(modelKey)
        ? prev.allowed_models.filter(m => m !== modelKey)
        : [...prev.allowed_models, modelKey]
    }));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="animate-spin text-white/40" size={24} />
    </div>
  );

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Gestione Utenti</h2>
        <Button onClick={handleCreate} className="bg-white text-black hover:bg-white/90" data-testid="create-user-button">
          <Plus size={16} className="mr-2" /> Nuovo Utente
        </Button>
      </div>

      <Card className="bg-[#101013] border-white/[0.08]">
        <CardContent className="p-0">
          <Table data-testid="admin-users-table">
            <TableHeader>
              <TableRow className="border-white/[0.06] hover:bg-transparent">
                <TableHead className="text-white/50">Utente</TableHead>
                <TableHead className="text-white/50">Ruolo</TableHead>
                <TableHead className="text-white/50">Crediti</TableHead>
                <TableHead className="text-white/50">Budget mensile</TableHead>
                <TableHead className="text-white/50">Modelli</TableHead>
                <TableHead className="text-white/50">Stato</TableHead>
                <TableHead className="text-white/50 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(user => (
                <TableRow key={user.id} className="border-white/[0.06] hover:bg-white/[0.02]">
                  <TableCell>
                    <div>
                      <div className="text-white text-sm font-medium">{user.name}</div>
                      <div className="text-white/40 text-xs">{user.email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} 
                      className={user.role === 'admin' ? 'bg-white text-black' : 'bg-white/10 text-white/60'}>
                      {user.role === 'admin' ? 'Admin' : 'Utente'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-white/70 text-sm" data-testid="credits-remaining-value">
                      {((user.credits_total || 0) - (user.credits_used || 0)).toFixed(1)} / {(user.credits_total || 0).toFixed(0)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-white/70 text-sm">
                      {(user.monthly_used || 0).toFixed(1)} / {(user.monthly_limit || 0).toFixed(0)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(user.allowed_models || []).slice(0, 3).map(m => (
                        <Badge key={m} variant="outline" className="text-xs border-white/10 text-white/50">{m}</Badge>
                      ))}
                      {(user.allowed_models || []).length > 3 && (
                        <Badge variant="outline" className="text-xs border-white/10 text-white/40">
                          +{user.allowed_models.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={user.active !== false ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}>
                      {user.active !== false ? 'Attivo' : 'Disattivato'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleResetCredits(user.id)}
                        className="h-8 w-8 text-white/30 hover:text-white/70 hover:bg-white/[0.06]">
                        <RotateCcw size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(user)}
                        className="h-8 w-8 text-white/30 hover:text-white/70 hover:bg-white/[0.06]">
                        <Pencil size={14} />
                      </Button>
                      {user.role !== 'admin' && (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)}
                          className="h-8 w-8 text-red-400/50 hover:text-red-400 hover:bg-red-400/10">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#141418] border-white/[0.08] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editUser ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/60">Nome</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-white/[0.04] border-white/[0.08] text-white" data-testid="user-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Email</Label>
                <Input value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                  type="email" className="bg-white/[0.04] border-white/[0.08] text-white" data-testid="user-email-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/60">{editUser ? 'Nuova password (lascia vuoto per non cambiare)' : 'Password'}</Label>
                <Input value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})}
                  type="password" className="bg-white/[0.04] border-white/[0.08] text-white" data-testid="user-password-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Ruolo</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                  <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/[0.08]">
                    <SelectItem value="user" className="text-white focus:bg-white/[0.06] focus:text-white">Utente</SelectItem>
                    <SelectItem value="admin" className="text-white focus:bg-white/[0.06] focus:text-white">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator className="bg-white/[0.06]" />

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-white/60">Crediti totali</Label>
                <Input type="number" value={formData.credits_total} 
                  onChange={(e) => setFormData({...formData, credits_total: parseFloat(e.target.value) || 0})}
                  className="bg-white/[0.04] border-white/[0.08] text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Budget mensile</Label>
                <Input type="number" value={formData.monthly_limit}
                  onChange={(e) => setFormData({...formData, monthly_limit: parseFloat(e.target.value) || 0})}
                  className="bg-white/[0.04] border-white/[0.08] text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Msg/giorno</Label>
                <Input type="number" value={formData.daily_message_limit}
                  onChange={(e) => setFormData({...formData, daily_message_limit: parseInt(e.target.value) || 0})}
                  className="bg-white/[0.04] border-white/[0.08] text-white" />
              </div>
            </div>

            <Separator className="bg-white/[0.06]" />

            <div className="space-y-2">
              <Label className="text-white/60">Modelli AI disponibili</Label>
              <div className="grid grid-cols-2 gap-2">
                {allModels.map(m => (
                  <button
                    key={m.key}
                    onClick={() => toggleModel(m.key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors border ${
                      formData.allowed_models.includes(m.key) 
                        ? 'bg-white/10 border-white/20 text-white' 
                        : 'bg-white/[0.02] border-white/[0.06] text-white/40 hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className={`h-3 w-3 rounded-sm border ${
                      formData.allowed_models.includes(m.key) ? 'bg-white border-white' : 'border-white/20'
                    }`} />
                    {m.name}
                  </button>
                ))}
              </div>
            </div>

            {editUser && (
              <div className="flex items-center justify-between">
                <Label className="text-white/60">Account attivo</Label>
                <Switch checked={formData.active} onCheckedChange={(v) => setFormData({...formData, active: v})} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} 
              className="border-white/10 text-white/60 hover:bg-white/[0.04]">
              Annulla
            </Button>
            <Button onClick={handleSave} className="bg-white text-black hover:bg-white/90" data-testid="save-user-button">
              {editUser ? 'Aggiorna' : 'Crea utente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AnalyticsTab() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminGetAnalytics()
      .then(setAnalytics)
      .catch(() => toast.error('Errore nel caricamento analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="animate-spin text-white/40" size={24} />
    </div>
  );

  if (!analytics) return null;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard icon={Users} label="Utenti totali" value={analytics.total_users} />
        <KPICard icon={Activity} label="Utenti attivi" value={analytics.active_users} />
        <KPICard icon={MessageSquare} label="Conversazioni" value={analytics.total_conversations} />
        <KPICard icon={CreditCard} label="Messaggi totali" value={analytics.total_messages} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily usage chart */}
        <Card className="bg-[#101013] border-white/[0.08]">
          <CardHeader>
            <CardTitle className="text-white text-sm font-medium">Messaggi per giorno</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.daily_usage && analytics.daily_usage.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={analytics.daily_usage}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
                  <ChartTooltip contentStyle={{ background: '#1a1a1e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'white' }} />
                  <Area type="monotone" dataKey="count" stroke="rgba(255,255,255,0.6)" fill="rgba(255,255,255,0.08)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-white/30 text-sm">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>

        {/* Model usage chart */}
        <Card className="bg-[#101013] border-white/[0.08]">
          <CardHeader>
            <CardTitle className="text-white text-sm font-medium">Utilizzo per modello</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.model_usage && Object.keys(analytics.model_usage).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={Object.entries(analytics.model_usage).map(([name, count]) => ({ name, count }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
                  <ChartTooltip contentStyle={{ background: '#1a1a1e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'white' }} />
                  <Bar dataKey="count" fill="rgba(255,255,255,0.3)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-white/30 text-sm">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User stats table */}
      <Card className="bg-[#101013] border-white/[0.08]">
        <CardHeader>
          <CardTitle className="text-white text-sm font-medium">Statistiche per utente</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/[0.06] hover:bg-transparent">
                <TableHead className="text-white/50">Utente</TableHead>
                <TableHead className="text-white/50">Messaggi</TableHead>
                <TableHead className="text-white/50">Crediti usati</TableHead>
                <TableHead className="text-white/50">Budget mensile usato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(analytics.user_stats || []).map(u => (
                <TableRow key={u.id} className="border-white/[0.06] hover:bg-white/[0.02]">
                  <TableCell>
                    <div>
                      <div className="text-white text-sm">{u.name}</div>
                      <div className="text-white/40 text-xs">{u.email}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-white/70 text-sm">{u.messages}</TableCell>
                  <TableCell className="text-white/70 text-sm">
                    {(u.credits_used || 0).toFixed(2)} / {(u.credits_total || 0).toFixed(0)}
                  </TableCell>
                  <TableCell className="text-white/70 text-sm">
                    {(u.monthly_used || 0).toFixed(2)} / {(u.monthly_limit || 0).toFixed(0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function KPICard({ icon: Icon, label, value }) {
  return (
    <Card className="bg-[#101013] border-white/[0.08]">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-lg bg-white/[0.06] flex items-center justify-center">
            <Icon size={16} className="text-white/50" />
          </div>
        </div>
        <div className="text-2xl font-bold text-white">{value || 0}</div>
        <div className="text-xs text-white/40 mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}
